import React, { useState, useEffect, useRef } from 'react';
import { supabaseClient } from '../../services/supabase';

// 画像リサイズ関数
const resizeImage = (file, maxWidth = 1200, maxHeight = 1200, quality = 0.8) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;

        // アスペクト比を維持してリサイズ
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Canvas to Blob failed'));
            }
          },
          'image/jpeg',
          quality
        );
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

function SeasonalManagement() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    comment: '',
    display_order: 0,
    is_visible: true,
    imageFile: null,
    imagePreview: null
  });
  const fileInputRef = useRef(null);
  const modalRef = useRef(null);

  useEffect(() => {
    loadItems();
  }, []);

  // モーダルが開いたときに自動スクロール
  useEffect(() => {
    if (isModalOpen && modalRef.current) {
      setTimeout(() => {
        modalRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }, 100);
    }
  }, [isModalOpen]);

  const loadItems = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabaseClient
        .from('seasonal_items')
        .select('*')
        .order('display_order', { ascending: true });

      if (error) throw error;
      setItems(data || []);
    } catch (error) {
      console.error('読み込みエラー:', error);
      alert('データの読み込みに失敗しました。');
    } finally {
      setLoading(false);
    }
  };

  const openModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        comment: item.comment || '',
        display_order: item.display_order || 0,
        is_visible: item.is_visible !== false,
        imageFile: null,
        imagePreview: item.image_url
      });
    } else {
      setEditingItem(null);
      setFormData({
        comment: '',
        display_order: items.length,
        is_visible: true,
        imageFile: null,
        imagePreview: null
      });
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingItem(null);
    setFormData({
      comment: '',
      display_order: 0,
      is_visible: true,
      imageFile: null,
      imagePreview: null
    });
  };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // ファイルサイズチェック（10MB以下）
    if (file.size > 10 * 1024 * 1024) {
      alert('ファイルサイズは10MB以下にしてください。');
      return;
    }

    // プレビュー表示
    const reader = new FileReader();
    reader.onload = (e) => {
      setFormData(prev => ({
        ...prev,
        imageFile: file,
        imagePreview: e.target.result
      }));
    };
    reader.readAsDataURL(file);
  };

  const uploadImage = async (file) => {
    // 画像をリサイズ
    const resizedBlob = await resizeImage(file, 1200, 1200, 0.85);
    
    // ファイル名を生成
    const fileName = `seasonal_${Date.now()}_${Math.random().toString(36).substr(2, 9)}.jpg`;
    
    // Supabase Storageにアップロード
    const { error } = await supabaseClient.storage
      .from('seasonal-images')
      .upload(fileName, resizedBlob, {
        contentType: 'image/jpeg',
        upsert: false
      });

    if (error) throw error;

    // 公開URLを取得
    const { data: urlData } = supabaseClient.storage
      .from('seasonal-images')
      .getPublicUrl(fileName);

    return urlData.publicUrl;
  };

  const handleSave = async () => {
    if (!formData.imagePreview && !editingItem) {
      alert('画像を選択してください。');
      return;
    }

    setUploading(true);
    try {
      let imageUrl = editingItem?.image_url;

      // 新しい画像がある場合はアップロード
      if (formData.imageFile) {
        imageUrl = await uploadImage(formData.imageFile);
      }

      const itemData = {
        image_url: imageUrl,
        comment: formData.comment,
        display_order: parseInt(formData.display_order) || 0,
        is_visible: formData.is_visible,
        updated_at: new Date().toISOString()
      };

      if (editingItem) {
        // 更新
        const { error } = await supabaseClient
          .from('seasonal_items')
          .update(itemData)
          .eq('id', editingItem.id);

        if (error) throw error;
        alert('更新しました。');
      } else {
        // 新規作成
        const { error } = await supabaseClient
          .from('seasonal_items')
          .insert(itemData);

        if (error) throw error;
        alert('作成しました。');
      }

      closeModal();
      loadItems();
    } catch (error) {
      console.error('保存エラー:', error);
      alert('保存に失敗しました。\n' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`「${item.comment || '(コメントなし)'}」を削除しますか？`)) {
      return;
    }

    try {
      // データベースから削除
      const { error } = await supabaseClient
        .from('seasonal_items')
        .delete()
        .eq('id', item.id);

      if (error) throw error;

      // 画像も削除（オプション）
      if (item.image_url) {
        const fileName = item.image_url.split('/').pop();
        await supabaseClient.storage
          .from('seasonal-images')
          .remove([fileName]);
      }

      alert('削除しました。');
      loadItems();
    } catch (error) {
      console.error('削除エラー:', error);
      alert('削除に失敗しました。');
    }
  };

  const toggleVisibility = async (item) => {
    try {
      const { error } = await supabaseClient
        .from('seasonal_items')
        .update({ is_visible: !item.is_visible })
        .eq('id', item.id);

      if (error) throw error;
      loadItems();
    } catch (error) {
      console.error('更新エラー:', error);
      alert('更新に失敗しました。');
    }
  };

  return (
    <section className="admin-section">
      <div className="container">
        <h2>旬のおすすめ管理</h2>

        <div className="action-bar" style={{ marginBottom: '20px' }}>
          <button className="btn-primary" onClick={() => openModal()}>
            + 新規追加
          </button>
        </div>

        {loading ? (
          <p>読み込み中...</p>
        ) : items.length === 0 ? (
          <p style={{ color: '#888' }}>コンテンツがありません。「新規追加」から追加してください。</p>
        ) : (
          <div className="seasonal-admin-grid">
            {items.map((item) => (
              <div 
                key={item.id} 
                className={`seasonal-admin-card ${!item.is_visible ? 'hidden-item' : ''}`}
              >
                <div className="seasonal-admin-image">
                  <img src={item.image_url} alt={item.comment || '旬のおすすめ'} />
                  {!item.is_visible && (
                    <div className="hidden-badge">非表示</div>
                  )}
                </div>
                <div className="seasonal-admin-info">
                  <p className="seasonal-admin-comment">{item.comment || '(コメントなし)'}</p>
                  <p className="seasonal-admin-order">表示順: {item.display_order}</p>
                </div>
                <div className="seasonal-admin-actions">
                  <button 
                    className={`btn btn-sm ${item.is_visible ? 'btn-warning' : 'btn-success'}`}
                    onClick={() => toggleVisibility(item)}
                  >
                    {item.is_visible ? '非表示' : '表示'}
                  </button>
                  <button 
                    className="btn-small btn-edit"
                    onClick={() => openModal(item)}
                  >
                    編集
                  </button>
                  <button 
                    className="btn-small btn-delete"
                    onClick={() => handleDelete(item)}
                  >
                    削除
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* 編集モーダル */}
        {isModalOpen && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content" ref={modalRef} onClick={(e) => e.stopPropagation()}>
              <h3>{editingItem ? '編集' : '新規追加'}</h3>
              
              <div className="form-group">
                <label>画像 *</label>
                <div className="image-upload-area">
                  {formData.imagePreview ? (
                    <div className="image-preview">
                      <img src={formData.imagePreview} alt="プレビュー" />
                      <button 
                        type="button" 
                        className="btn-change-image"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        画像を変更
                      </button>
                    </div>
                  ) : (
                    <div 
                      className="image-dropzone"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <p>クリックして画像を選択</p>
                      <p className="hint">JPEG, PNG, WebP（最大10MB）</p>
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleImageChange}
                    style={{ display: 'none' }}
                  />
                </div>
                <p className="form-hint">※アップロード時に自動でリサイズされます（最大1200px）</p>
              </div>

              <div className="form-group">
                <label>コメント</label>
                <textarea
                  value={formData.comment}
                  onChange={(e) => setFormData(prev => ({ ...prev, comment: e.target.value }))}
                  placeholder="旬のおすすめについてのコメント"
                  rows={3}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>表示順</label>
                  <input
                    type="number"
                    value={formData.display_order}
                    onChange={(e) => setFormData(prev => ({ ...prev, display_order: e.target.value }))}
                    min={0}
                  />
                  <p className="form-hint">※数字が小さいほど先に表示されます</p>
                </div>

                <div className="form-group">
                  <label>表示設定</label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.is_visible}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_visible: e.target.checked }))}
                    />
                    サイトに表示する
                  </label>
                </div>
              </div>

              <div className="modal-actions">
                <button 
                  className="btn-secondary" 
                  onClick={closeModal}
                  disabled={uploading}
                >
                  キャンセル
                </button>
                <button 
                  className="btn-primary" 
                  onClick={handleSave}
                  disabled={uploading}
                >
                  {uploading ? '保存中...' : '保存'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .seasonal-admin-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 20px;
        }

        .seasonal-admin-card {
          background: #1a1a1a;
          border-radius: 10px;
          overflow: hidden;
          border: 1px solid #333;
        }

        .seasonal-admin-card.hidden-item {
          opacity: 0.6;
        }

        .seasonal-admin-image {
          position: relative;
          width: 100%;
          height: 180px;
          overflow: hidden;
        }

        .seasonal-admin-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .hidden-badge {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: rgba(100, 100, 100, 0.9);
          color: white;
          padding: 8px 16px;
          border-radius: 4px;
          font-size: 14px;
          font-weight: bold;
        }

        .seasonal-admin-info {
          padding: 15px;
        }

        .seasonal-admin-comment {
          color: #ffffff;
          margin-bottom: 8px;
          line-height: 1.4;
        }

        .seasonal-admin-order {
          color: #888;
          font-size: 0.9rem;
        }

        .seasonal-admin-actions {
          display: flex;
          gap: 8px;
          padding: 15px;
          border-top: 1px solid #333;
          flex-wrap: wrap;
        }

        .btn-small {
          padding: 6px 12px;
          font-size: 0.85rem;
          border: none;
          border-radius: 4px;
          cursor: pointer;
        }

        .btn-toggle {
          background: #555;
          color: white;
        }

        .btn-delete {
          background: #dc3545;
          color: white;
        }

        .image-upload-area {
          margin-top: 10px;
        }

        .image-preview {
          position: relative;
          max-width: 300px;
        }

        .image-preview img {
          width: 100%;
          border-radius: 8px;
        }

        .btn-change-image {
          display: block;
          margin-top: 10px;
          padding: 8px 16px;
          background: #2196F3;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 0.9rem;
        }

        .btn-change-image:hover {
          background: #1976D2;
        }

        .image-dropzone {
          border: 2px dashed #444;
          border-radius: 8px;
          padding: 40px;
          text-align: center;
          cursor: pointer;
          transition: border-color 0.3s ease;
        }

        .image-dropzone:hover {
          border-color: #666;
        }

        .image-dropzone p {
          color: #888;
          margin: 5px 0;
        }

        .image-dropzone .hint {
          font-size: 0.85rem;
          color: #666;
        }

        .form-hint {
          font-size: 0.8rem;
          color: #888;
          margin-top: 5px;
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #ffffff;
          cursor: pointer;
        }

        .checkbox-label input {
          width: 18px;
          height: 18px;
        }
      `}</style>
    </section>
  );
}

export default SeasonalManagement;

